import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Alert,
  Stack,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Divider,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { fetchJson } from '../../../utils/fetchJson';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function SubmitTicketPage() {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [attachments, setAttachments] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const apiBase = (() => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
    return String(baseRaw || '').replace(/\/$/, '');
  })();
  const resolveAttachmentUrl = useCallback((raw) => {
    const u = raw?.url || raw?.path || '';
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    if (apiBase && u.startsWith('/')) return `${apiBase}${u}`;
    return u;
  }, [apiBase]);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await fetchJson('/api/support/tickets');
      setTickets(Array.isArray(res?.tickets) ? res.tickets : []);
    } catch (_) {
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openTicket = useCallback(async (ticket) => {
    const id = ticket?.id;
    if (!id) return;
    setError(null);
    try {
      const res = await fetchJson(`/api/support/tickets/${id}`);
      setActiveTicket(res?.ticket || ticket);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
      setMessageText('');
      setMessageAttachments([]);
    } catch (e) {
      setError(e?.message || 'Failed to load ticket');
    }
  }, []);

  const closeTicket = () => {
    setActiveTicket(null);
    setMessages([]);
    setMessageText('');
    setMessageAttachments([]);
  };

  const refreshTicket = useCallback(async () => {
    if (!activeTicket?.id) return;
    try {
      const res = await fetchJson(`/api/support/tickets/${activeTicket.id}`);
      setActiveTicket(res?.ticket || activeTicket);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
    } catch (_) {}
  }, [activeTicket]);

  const sendMessage = async () => {
    if (!activeTicket?.id) return;
    if (!messageText && (!messageAttachments || messageAttachments.length === 0)) return;
    setSending(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('message', messageText);
      (messageAttachments || []).forEach((f) => form.append('attachments', f));
      await fetchJson(`/api/support/tickets/${activeTicket.id}/messages`, {
        method: 'POST',
        body: form
      });
      setMessageText('');
      setMessageAttachments([]);
      await refreshTicket();
      await loadTickets();
    } catch (e) {
      setError(e?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const activeTicketId = String(activeTicket?.id || '');
  const sortedTickets = useMemo(() => {
    return Array.isArray(tickets)
      ? [...tickets].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      })
      : [];
  }, [tickets]);

  const handleFiles = (files) => {
    const list = Array.from(files || []);
    const next = [...attachments];
    for (const file of list) {
      if (file.size > MAX_FILE_SIZE) {
        setError(t('support.submitTicket.errors.fileTooLarge', { name: file.name }));
        continue;
      }
      if (next.length >= MAX_FILES) break;
      next.push(file);
    }
    setAttachments(next.slice(0, MAX_FILES));
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const form = new FormData();
      form.append('subject', subject);
      form.append('description', description);
      form.append('priority', priority);
      form.append('category', category);
      attachments.forEach((file) => form.append('attachments', file));

      const resp = await fetchJson('/api/support/tickets', {
        method: 'POST',
        body: form
      });
      setResult(resp?.ticket || null);
      setSubject('');
      setDescription('');
      setPriority('medium');
      setCategory('general');
      setAttachments([]);
      await loadTickets();
      if (resp?.ticket?.id) {
        await openTicket(resp.ticket);
      }
    } catch (err) {
      setError(err?.message || t('support.submitTicket.errors.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">My Tickets</Typography>
            <Button size="small" variant={activeTicket ? 'outlined' : 'contained'} onClick={closeTicket}>
              New
            </Button>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {ticketsLoading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : (
            <List dense sx={{ p: 0 }}>
              {sortedTickets.length === 0 ? (
                <Typography color="text.secondary">No tickets yet</Typography>
              ) : (
                sortedTickets.map((tk) => {
                  const selected = String(tk?.id || '') === activeTicketId;
                  const primary = tk?.subject || tk?.ticketNumber || 'Ticket';
                  const secondaryParts = [];
                  if (tk?.ticketNumber) secondaryParts.push(tk.ticketNumber);
                  if (tk?.status) secondaryParts.push(tk.status);
                  if (tk?.priority) secondaryParts.push(tk.priority);
                  return (
                    <ListItemButton
                      key={tk.id}
                      selected={selected}
                      onClick={() => openTicket(tk)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={primary}
                        secondary={secondaryParts.join(' • ')}
                        primaryTypographyProps={{ noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>
          )}
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" gutterBottom>
            {t('support.submitTicket.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('support.submitTicket.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('support.submitTicket.messages.submitted', { ticketNumber: result.ticketNumber })}
            </Alert>
          )}

          {activeTicket ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box>
                    <Typography variant="h6">
                      {activeTicket?.ticketNumber || 'Ticket'}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {activeTicket?.subject || ''}
                    </Typography>
                    {activeTicket?.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(activeTicket.createdAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {activeTicket?.status && <Chip size="small" label={activeTicket.status} />}
                    {activeTicket?.priority && <Chip size="small" label={activeTicket.priority} />}
                    {activeTicket?.category && <Chip size="small" label={activeTicket.category} />}
                    <Button size="small" variant="outlined" onClick={refreshTicket}>
                      Refresh
                    </Button>
                  </Stack>
                </Stack>

                {activeTicket?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {activeTicket.description}
                  </Typography>
                )}

                {Array.isArray(activeTicket?.attachments) && activeTicket.attachments.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {activeTicket.attachments.map((a, idx) => (
                      <Chip
                        key={`tk-att-${idx}`}
                        label={a?.originalName || a?.filename || a?.url || 'attachment'}
                        component="a"
                        href={resolveAttachmentUrl(a)}
                        target="_blank"
                        clickable
                      />
                    ))}
                  </Stack>
                )}

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, maxHeight: 380, overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <Typography color="text.secondary">No messages</Typography>
                  ) : (
                    messages.map((m) => (
                      <Box key={m.id} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {m.sender?.name || m.sender?.email || `User #${m.senderUserId || ''}`}
                          {m.createdAt ? ` • ${new Date(m.createdAt).toLocaleString()}` : ''}
                        </Typography>
                        <Typography variant="body1">{m.message}</Typography>
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {m.attachments.map((a, idx) => (
                              <Chip
                                key={`${m.id}-att-${idx}`}
                                label={a?.originalName || a?.filename || a?.url || 'attachment'}
                                component="a"
                                href={resolveAttachmentUrl(a)}
                                target="_blank"
                                clickable
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))
                  )}
                </Box>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <TextField
                    label="Reply"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                    Attach
                    <input
                      hidden
                      multiple
                      type="file"
                      onChange={(e) => setMessageAttachments(Array.from(e.target.files || []))}
                      accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                  </Button>
                  <Button variant="contained" onClick={sendMessage} disabled={sending}>
                    {sending ? 'Sending…' : 'Send'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label={t('support.submitTicket.fields.subject')}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label={t('support.submitTicket.fields.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    required
                    multiline
                    minRows={5}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label={t('support.submitTicket.fields.priority')}
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="low">{t('support.submitTicket.options.priority.low')}</MenuItem>
                      <MenuItem value="medium">{t('support.submitTicket.options.priority.medium')}</MenuItem>
                      <MenuItem value="high">{t('support.submitTicket.options.priority.high')}</MenuItem>
                      <MenuItem value="urgent">{t('support.submitTicket.options.priority.urgent')}</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label={t('support.submitTicket.fields.category')}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="general">{t('support.submitTicket.options.category.general')}</MenuItem>
                      <MenuItem value="billing">{t('support.submitTicket.options.category.billing')}</MenuItem>
                      <MenuItem value="permissions">{t('support.submitTicket.options.category.permissions')}</MenuItem>
                      <MenuItem value="phases">{t('support.submitTicket.options.category.phases')}</MenuItem>
                      <MenuItem value="technical">{t('support.submitTicket.options.category.technical')}</MenuItem>
                      <MenuItem value="reporting">{t('support.submitTicket.options.category.reporting')}</MenuItem>
                    </TextField>
                  </Stack>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('support.submitTicket.attachments.title')}
                    </Typography>
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ mb: 1 }}>
                      {t('support.submitTicket.attachments.upload')}
                      <input
                        hidden
                        type="file"
                        multiple
                        onChange={(e) => handleFiles(e.target.files)}
                        accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      />
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('support.submitTicket.attachments.helper', { maxFiles: MAX_FILES })}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                      {attachments.map((file, idx) => (
                        <Chip
                          key={`${file.name}-${idx}`}
                          label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                          onDelete={() => removeAttachment(idx)}
                          deleteIcon={
                            <IconButton size="small" component="span">
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          }
                        />
                      ))}
                    </Stack>
                  </Box>
                  {loading && <LinearProgress />}
                  <Box>
                    <Button type="submit" variant="contained" disabled={loading}>
                      {loading ? t('support.submitTicket.actions.submitting') : t('support.submitTicket.actions.submit')}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
